'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import { useLocale } from 'next-intl'
import Fuse from 'fuse.js'
import { Search, Play, FileText, Keyboard } from '@/components/ui/icons'
import { toDocsLocale, type DocsLocale } from '@/lib/docs'
import { buildLearnSearchIndex, type LearnSearchItem } from '@/lib/docs/learnSearchIndex'

type SearchItem = LearnSearchItem

const SEARCH_COPY: Record<
  DocsLocale,
  {
    placeholder: string
    ariaLabel: string
    empty: string
    noResultsPrefix: string
    move: string
    open: string
    results: string
    categories: Record<SearchItem['category'], string>
  }
> = {
  en: {
    // i18n-ignore -- locale-keyed translation table (these strings are the source copy)
    placeholder: 'Search lessons, docs, shortcuts...',
    // i18n-ignore -- locale-keyed translation table (these strings are the source copy)
    ariaLabel: 'Search lessons, docs, and shortcuts',
    empty: 'Search lessons, docs, and shortcuts',
    noResultsPrefix: 'No results for',
    move: 'move',
    open: 'open',
    results: 'results',
    categories: {
      video: 'Video lessons',
      doc: 'Documentation',
      shortcut: 'Shortcuts',
    },
  },
  ja: {
    placeholder: 'レッスン、ドキュメント、ショートカットを検索...',
    ariaLabel: 'レッスン、ドキュメント、ショートカットを検索',
    empty: 'レッスン、ドキュメント、ショートカットを検索',
    noResultsPrefix: '検索結果はありません:',
    move: '移動',
    open: '開く',
    results: '件',
    categories: {
      video: '動画レッスン',
      doc: 'ドキュメント',
      shortcut: 'ショートカット',
    },
  },
  // TODO: Arabic copy needs Koh review — machine-translated initial pass.
  ar: {
    placeholder: 'ابحث في الدروس والوثائق والاختصارات...',
    ariaLabel: 'ابحث في الدروس والوثائق والاختصارات',
    empty: 'ابحث في الدروس والوثائق والاختصارات',
    noResultsPrefix: 'لا توجد نتائج لـ',
    move: 'تنقّل',
    open: 'فتح',
    results: 'نتائج',
    categories: {
      video: 'دروس الفيديو',
      doc: 'الوثائق',
      shortcut: 'الاختصارات',
    },
  },
  es: {
    // i18n-ignore -- locale-keyed translation table (these strings are the source copy)
    placeholder: 'Busca lecciones, documentación, atajos...',
    // i18n-ignore -- locale-keyed translation table (these strings are the source copy)
    ariaLabel: 'Busca lecciones, documentación y atajos',
    empty: 'Busca lecciones, documentación y atajos',
    noResultsPrefix: 'Sin resultados para',
    move: 'mover',
    open: 'abrir',
    results: 'resultados',
    categories: {
      video: 'Lecciones en video',
      doc: 'Documentación',
      shortcut: 'Atajos',
    },
  },
  ko: {
    placeholder: '레슨, 문서, 단축키 검색...',
    ariaLabel: '레슨, 문서, 단축키 검색',
    empty: '레슨, 문서, 단축키 검색',
    noResultsPrefix: '검색 결과 없음:',
    move: '이동',
    open: '열기',
    results: '개',
    categories: {
      video: '동영상 레슨',
      doc: '문서',
      shortcut: '단축키',
    },
  },
}

const CATEGORY_ICONS: Record<SearchItem['category'], typeof Play> = {
  video: Play,
  doc: FileText,
  shortcut: Keyboard,
}

const CATEGORY_ORDER: SearchItem['category'][] = ['video', 'doc', 'shortcut']

const KEY_LABELS: Record<string, string> = {
  Cmd: '⌘',
  Shift: 'Shift',
  Alt: '⌥',
  Ctrl: '⌃',
  ArrowLeft: '←',
  ArrowRight: '→',
  ArrowUp: '↑',
  ArrowDown: '↓',
  Delete: '⌫',
  Escape: 'Esc',
  Space: '␣',
}

function ShortcutKbd({ shortcut }: { shortcut: string }) {
  const groups = shortcut.split(' / ')
  return (
    <span className="inline-flex items-center gap-1">
      {groups.map((group, gi) => (
        <span key={gi} className="inline-flex items-center gap-0.5">
          {gi > 0 && <span className="text-muted-foreground/40 text-xs">/</span>}
          {group.includes('+') && group.length > 1 ? (
            group.split('+').map((k, ki) => (
              <kbd
                key={ki}
                className="inline-flex min-w-[20px] items-center justify-center rounded border border-border bg-secondary/80 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
              >
                {KEY_LABELS[k] ?? k}
              </kbd>
            ))
          ) : (
            <kbd className="inline-flex min-w-[20px] items-center justify-center rounded border border-border bg-secondary/80 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
              {KEY_LABELS[group] ?? group}
            </kbd>
          )}
        </span>
      ))}
    </span>
  )
}

const MAX_RESULTS = 10

export function LearnSearch() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const router = useRouter()
  const locale = toDocsLocale(useLocale())
  const copy = SEARCH_COPY[locale]
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const index = useMemo(() => buildLearnSearchIndex(locale), [locale])

  const fuse = useMemo(
    () =>
      new Fuse(index, {
        keys: [
          { name: 'title', weight: 3 },
          { name: 'description', weight: 1.5 },
          { name: 'meta', weight: 1 },
          { name: 'section', weight: 0.5 },
          { name: 'searchText', weight: 1.25 },
        ],
        threshold: 0.35,
        distance: 200,
        includeScore: true,
        ignoreLocation: true,
        minMatchCharLength: 2,
      }),
    [index],
  )

  const results = useMemo(() => {
    if (!query.trim()) return []
    return fuse.search(query, { limit: MAX_RESULTS }).map(r => r.item)
  }, [fuse, query])

  const grouped = useMemo(() => {
    const groups: Array<{
      category: SearchItem['category']
      label: string
      items: Array<{ idx: number; item: SearchItem }>
    }> = []
    let idx = 0
    for (const cat of CATEGORY_ORDER) {
      const members: Array<{ idx: number; item: SearchItem }> = []
      for (const item of results) {
        if (item.category === cat) {
          members.push({ idx, item })
          idx++
        }
      }
      if (members.length) groups.push({ category: cat, label: copy.categories[cat], items: members })
    }
    return groups
  }, [copy.categories, results])

  const flatResults = useMemo(() => grouped.flatMap(g => g.items.map(i => i.item)), [grouped])
  const currentActiveIndex = flatResults.length > 0 ? Math.min(activeIndex, flatResults.length - 1) : 0

  const openSearch = useCallback(() => {
    setQuery('')
    setActiveIndex(0)
    setOpen(true)
  }, [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isSlash = e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey
      const isCmdK = e.key === 'k' && (e.metaKey || e.ctrlKey)
      if (isSlash || isCmdK) {
        const tag = (e.target as HTMLElement)?.tagName
        if (isSlash && (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT')) return
        e.preventDefault()
        if (open) {
          setOpen(false)
        } else {
          openSearch()
        }
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, openSearch])

  useEffect(() => {
    if (open) {
      inputRef.current?.focus({ preventScroll: true })
      const frame = requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }))
      return () => cancelAnimationFrame(frame)
    }
  }, [open])

  useEffect(() => {
    const list = listRef.current
    if (!list) return
    const node = list.querySelector<HTMLButtonElement>(`[data-idx="${currentActiveIndex}"]`)
    node?.scrollIntoView({ block: 'nearest' })
  }, [currentActiveIndex])

  const navigate = useCallback(
    (href: string) => {
      setOpen(false)
      router.push(href)
    },
    [router],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex(i => (flatResults.length === 0 ? 0 : Math.min(flatResults.length - 1, i + 1)))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex(i => Math.max(0, i - 1))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const item = flatResults[currentActiveIndex]
        if (item) navigate(item.href)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
      }
    },
    [currentActiveIndex, flatResults, navigate],
  )

  if (!open) {
    return (
      <button
        onClick={openSearch}
        aria-label={copy.placeholder}
        // Collapses to an icon on small screens: the header also carries the
        // section tabs and the account CTA, and the placeholder is the only
        // part of this row that can be dropped without losing a destination.
        className="flex w-auto max-w-md items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-[13px] text-muted-foreground transition-colors hover:border-white/20 hover:bg-white/10 hover:text-foreground sm:w-full sm:px-4"
      >
        <Search className="h-3.5 w-3.5 shrink-0" />
        <span className="hidden flex-1 text-start sm:block">{copy.placeholder}</span>
        <kbd className="hidden rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground/60 sm:inline">
          /
        </kbd>
      </button>
    )
  }

  return (
    <>
      <button
        className="flex w-full max-w-md items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-1.5 text-[13px] text-muted-foreground"
        disabled
      >
        <Search className="h-3.5 w-3.5" />
        <span className="flex-1 text-start">{copy.placeholder}</span>
        <kbd className="hidden rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground/60 sm:inline">
          ⌘K
        </kbd>
      </button>

      {createPortal(
        <div
          className="fixed inset-0 z-[120] flex items-start justify-center pt-[18vh] bg-black/50"
          onClick={() => setOpen(false)}
        >
          <div
            role="dialog"
            aria-label={copy.ariaLabel}
            className="w-[660px] max-w-[94vw] max-h-[60vh] flex flex-col rounded-lg border border-border bg-popover shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
            onKeyDown={handleKeyDown}
          >
            <div className="flex shrink-0 items-center gap-3.5 px-5 h-16 border-b border-border">
              <Search className="w-[18px] h-[18px] text-muted-foreground shrink-0" strokeWidth={1.75} />
              <input
                ref={inputRef}
                value={query}
                onChange={e => {
                  setQuery(e.target.value)
                  setActiveIndex(0)
                }}
                aria-label={copy.ariaLabel}
                autoFocus
                placeholder={copy.placeholder}
                className="flex-1 appearance-none bg-transparent border-0 outline-none ring-0 text-[17px] text-foreground !caret-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0"
                style={{ outline: 'none', boxShadow: 'none', transition: 'none' }}
                autoComplete="off"
                spellCheck={false}
              />
              <kbd className="text-[10px] font-mono text-muted-foreground border border-border rounded px-1.5 py-0.5">
                esc
              </kbd>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto overflow-x-hidden py-1">
              {query.trim() === '' ? (
                <div className="px-3 py-8 text-center text-xs text-muted-foreground">{copy.empty}</div>
              ) : flatResults.length === 0 ? (
                <div className="px-3 py-8 text-center text-xs text-muted-foreground">
                  {/* eslint-disable-next-line i18next/no-literal-string -- decorative typographic quotes */}
                  {copy.noResultsPrefix} &ldquo;{query}&rdquo;
                </div>
              ) : (
                grouped.map(group => {
                  const Icon = CATEGORY_ICONS[group.category]
                  return (
                    <div key={group.category} className="py-1">
                      <div className="px-4 pb-0.5 text-[10px] font-medium text-muted-foreground/70 uppercase tracking-wider">
                        {group.label}
                      </div>
                      {group.items.map(({ idx, item }) => {
                        const active = idx === currentActiveIndex
                        return (
                          <button
                            key={item.id}
                            data-idx={idx}
                            role="option"
                            aria-selected={active}
                            onMouseEnter={() => setActiveIndex(idx)}
                            onClick={() => navigate(item.href)}
                            className={`w-full flex items-center gap-2.5 px-3 py-1.5 min-h-10 text-start text-xs ${
                              active ? 'bg-secondary text-foreground' : 'text-foreground/90 hover:bg-secondary/60'
                            }`}
                          >
                            <span className="w-8 h-8 shrink-0 rounded border border-border bg-muted/40 overflow-hidden flex items-center justify-center">
                              <Icon className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.75} />
                            </span>
                            {item.category === 'shortcut' ? (
                              <ShortcutKbd shortcut={item.title} />
                            ) : (
                              <span className="min-w-0 truncate">{item.title}</span>
                            )}
                            {item.meta && (
                              // eslint-disable-next-line i18next/no-literal-string -- decorative middle-dot separator
                              <span className="min-w-0 text-muted-foreground/70 truncate">· {item.meta}</span>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  )
                })
              )}
            </div>

            {flatResults.length > 0 && (
              <div className="flex items-center justify-between px-3 h-8 border-t border-border text-[10px] text-muted-foreground select-none">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <kbd className="font-mono border border-border rounded px-1">↑</kbd>
                    <kbd className="font-mono border border-border rounded px-1">↓</kbd>
                    {copy.move}
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="font-mono border border-border rounded px-1">↵</kbd>
                    {copy.open}
                  </span>
                </div>
                <span>
                  {locale === 'ja' ? `${flatResults.length}${copy.results}` : `${flatResults.length} ${copy.results}`}
                </span>
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
